import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCircleCheck,
  FaTriangleExclamation,
  FaCircleXmark,
  FaCircleInfo,
} from "react-icons/fa6";

/**
 * Модальне вікно підтвердження.
 *
 * @param {"success"|"warning"|"error"|"info"} status
 * @param {boolean}  processing   — коли true, кнопки блокуються й показується спінер
 */
const ConfirmDialog = ({
  status = "info",
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  processing = false,
}) => {
  const { t } = useTranslation();

  /* --- мапи іконок та стилів --- */
  const icons = {
    success: FaCircleCheck,
    warning: FaTriangleExclamation,
    error:   FaCircleXmark,
    info:    FaCircleInfo,
  };
  const Icon = icons[status] || FaCircleInfo;

  const iconColors = {
    success: "text-green-500",
    warning: "text-yellow-500",
    error:   "text-red-500",
    info:    "text-blue-500",
  };

  const confirmBg = {
    success: "bg-green-600 hover:bg-green-700",
    warning: "bg-yellow-600 hover:bg-yellow-700",
    error:   "bg-red-600   hover:bg-red-700",
    info:    "bg-blue-600  hover:bg-blue-700",
  };

  /* --- обробники --- */
  const handleConfirm = useCallback(() => {
    if (!processing) onConfirm?.();
  }, [onConfirm, processing]);

  const handleCancel = useCallback(() => {
    if (!processing) onCancel?.();
  }, [onCancel, processing]);

  /* --- спінер --- */
  const spinner = (
    <svg className="inline w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
    </svg>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* бекдроп з блюром */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={handleCancel}
      />

      {/* саме вікно */}
      <div className="relative w-full max-w-md mx-4 bg-gray-900 text-white p-6 rounded-2xl shadow-2xl">
        <div className="flex flex-col items-center text-center">
          <Icon className={`w-14 h-14 mb-4 ${iconColors[status]}`} />
          {title   && <h2 className="text-xl font-semibold">{t(title)}</h2>}
          {message && <p className="mt-2">{t(message)}</p>}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <button
            type="button"
            disabled={processing}
            onClick={handleCancel}
            className="py-2 rounded-xl bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
          >
            {cancelText || t("common.no")}
          </button>

          <button
            type="button"
            disabled={processing}
            onClick={handleConfirm}
            className={`py-2 rounded-xl disabled:opacity-50 ${confirmBg[status]}`}
          >
            {processing ? spinner : (confirmText || t("common.yes"))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
